import React, { useState } from 'react';
import { graphql, Link } from 'gatsby';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import moments from '../data/moments.json';

const styles = {
  intro: { maxWidth: '700px', marginBottom: '10px' },
  intro_p: { color: '#6b6b7d', fontSize: '16px', lineHeight: 1.7 },
  yearHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    marginTop: '30px',
    paddingBottom: '14px',
    borderBottom: '1px solid #eaeaf2',
    userSelect: 'none',
  },
  yearHeading: { fontSize: '22px', margin: 0 },
  chevron: { fontSize: '20px', color: '#6b6b7d', transition: 'transform 0.2s ease' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '24px',
    marginTop: '24px',
    marginBottom: '10px',
  },
  card: {
    background: '#f6f6fb',
    border: '1px solid #eaeaf2',
    borderRadius: '12px',
    overflow: 'hidden',
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
  },
  thumb: {
    width: '100%',
    aspectRatio: '4 / 3',
    objectFit: 'cover',
    display: 'block',
    background: '#f1dbe1',
  },
  meta: { padding: '14px 16px 16px' },
  tag: {
    display: 'inline-block',
    fontSize: '11px',
    fontWeight: 600,
    color: '#d6415c',
    background: '#fde5ea',
    padding: '2px 8px',
    borderRadius: '6px',
    marginBottom: '8px',
  },
  title: { fontSize: '15px', fontWeight: 600, marginBottom: '4px', lineHeight: 1.35 },
  excerpt: { fontSize: '13px', color: '#6b6b7d', margin: 0, lineHeight: 1.5 },

  sectionHeading: { fontSize: '26px', marginTop: '50px', marginBottom: '6px' },
  sectionSub: { color: '#6b6b7d', fontSize: '14px', marginBottom: '10px' },

  momentsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: '12px',
    marginTop: '24px',
    marginBottom: '10px',
  },
  momentTile: {
    position: 'relative',
    aspectRatio: '1',
    borderRadius: '10px',
    overflow: 'hidden',
    background: '#f1dbe1',
    cursor: 'pointer',
    border: 'none',
    padding: 0,
  },
  momentImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  momentCaption: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(to top, rgba(44,44,59,0.85), rgba(44,44,59,0))',
    color: '#fff',
    fontSize: '11px',
    fontWeight: 500,
    padding: '20px 8px 8px',
    lineHeight: 1.3,
    textAlign: 'left',
  },

  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(20, 20, 30, 0.9)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  overlayImg: { maxWidth: '90vw', maxHeight: '80vh', borderRadius: '8px', display: 'block' },
  overlayCaption: { color: '#fff', fontSize: '14px', marginTop: '14px', textAlign: 'center' },
  closeBtn: {
    position: 'absolute',
    top: '20px',
    right: '30px',
    color: '#fff',
    fontSize: '32px',
    cursor: 'pointer',
    lineHeight: 1,
    background: 'none',
    border: 'none',
  },
  navBtn: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#fff',
    fontSize: '40px',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: '10px 16px',
  },
  prevBtn: { left: '10px' },
  nextBtn: { right: '10px' },
};

const OurWork = ({ data }) => {
  const stories = data.stories.edges;

  // Group stories by year
  const groupedStories = {};
  stories.forEach(({ node }) => {
    const year = node.frontmatter.year;
    if (!groupedStories[year]) groupedStories[year] = [];
    groupedStories[year].push(node);
  });
  const storyYears = Object.keys(groupedStories).sort((a, b) => b - a);
  const [openStoryYear, setOpenStoryYear] = useState(storyYears[0]);
  const toggleStoryYear = (year) =>
    setOpenStoryYear((current) => (current === year ? null : year));

  // Group moments by year
  const groupedMoments = {};
  moments.forEach((m) => {
    if (!groupedMoments[m.year]) groupedMoments[m.year] = [];
    groupedMoments[m.year].push(m);
  });
  const momentYears = Object.keys(groupedMoments).sort((a, b) => b - a);
  const [openMomentYear, setOpenMomentYear] = useState(momentYears[0]);
  const toggleMomentYear = (year) =>
    setOpenMomentYear((current) => (current === year ? null : year));

  const [openMoment, setOpenMoment] = useState(null); // { year, index } or null
  const currentList = openMoment ? groupedMoments[openMoment.year] : null;

  const closeMoment = () => setOpenMoment(null);
  const showPrevMoment = (e) => {
    e.stopPropagation();
    setOpenMoment((cur) => ({
      year: cur.year,
      index: cur.index === 0 ? currentList.length - 1 : cur.index - 1,
    }));
  };
  const showNextMoment = (e) => {
    e.stopPropagation();
    setOpenMoment((cur) => ({
      year: cur.year,
      index: cur.index === currentList.length - 1 ? 0 : cur.index + 1,
    }));
  };

  return (
    <Layout bodyClass="page-our-work">
      <SEO title="Our Work" />
      <div className="container pt-6 pt-md-10 pb-6 pb-md-10">
        <div style={styles.intro}>
          <h1>Our Work, In Pictures</h1>
          <p style={styles.intro_p}>
            A look at the outreach drives, volunteer meets, school visits, and fundraisers that
            bring Hidden Voices' mission to life.
          </p>
        </div>

        {/* STORIES */}
        {storyYears.map((year) => {
          const isOpen = openStoryYear === year;
          return (
            <div key={year}>
              <div style={styles.yearHeader} onClick={() => toggleStoryYear(year)}>
                <h2 style={styles.yearHeading}>{year}</h2>
                <span
                  style={{
                    ...styles.chevron,
                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                >
                  ▾
                </span>
              </div>
              {isOpen && (
                <div style={styles.grid}>
                  {groupedStories[year].map((node) => (
                    <Link key={node.id} to={node.fields.slug} style={styles.card}>
                      {node.frontmatter.image && (
                        <img
                          src={node.frontmatter.image}
                          alt={node.frontmatter.title}
                          style={styles.thumb}
                        />
                      )}
                      <div style={styles.meta}>
                        {node.frontmatter.category && (
                          <span style={styles.tag}>{node.frontmatter.category}</span>
                        )}
                        <h3 style={styles.title}>{node.frontmatter.title}</h3>
                        <p style={styles.excerpt}>{node.excerpt}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* MOMENTS */}
        <h2 style={styles.sectionHeading}>Moments</h2>
        <p style={styles.sectionSub}>
          Quick snapshots — meetings, cameos, and small moments. Click any photo to view it larger.
        </p>

        {momentYears.map((year) => {
          const isOpen = openMomentYear === year;
          return (
            <div key={year}>
              <div style={styles.yearHeader} onClick={() => toggleMomentYear(year)}>
                <h3 style={{ ...styles.yearHeading, fontSize: '18px' }}>{year}</h3>
                <span
                  style={{
                    ...styles.chevron,
                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                >
                  ▾
                </span>
              </div>
              {isOpen && (
                <div style={styles.momentsGrid}>
                  {groupedMoments[year].map((m, index) => (
                    <button
                      key={index}
                      style={styles.momentTile}
                      onClick={() => setOpenMoment({ year, index })}
                    >
                      <img src={m.image} alt={m.caption || 'Moment photo'} style={styles.momentImg} />
                      {m.caption && <div style={styles.momentCaption}>{m.caption}</div>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {openMoment && (
        <div style={styles.overlay} onClick={closeMoment}>
          <button style={styles.closeBtn} onClick={closeMoment}>×</button>
          {currentList.length > 1 && (
            <button style={{ ...styles.navBtn, ...styles.prevBtn }} onClick={showPrevMoment}>‹</button>
          )}
          <img
            src={currentList[openMoment.index].image}
            alt={currentList[openMoment.index].caption || 'Moment photo'}
            style={styles.overlayImg}
            onClick={(e) => e.stopPropagation()}
          />
          {currentList[openMoment.index].caption && (
            <div style={styles.overlayCaption}>{currentList[openMoment.index].caption}</div>
          )}
          {currentList.length > 1 && (
            <button style={{ ...styles.navBtn, ...styles.nextBtn }} onClick={showNextMoment}>›</button>
          )}
        </div>
      )}
    </Layout>
  );
};

export const query = graphql`
  query OurWorkQuery {
    stories: allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "content/stories/.*/" } }
      sort: { fields: [frontmatter___weight], order: ASC }
    ) {
      edges {
        node {
          id
          excerpt(pruneLength: 90)
          fields {
            slug
          }
          frontmatter {
            title
            category
            image
            year: date(formatString: "YYYY")
          }
        }
      }
    }
  }
`;

export default OurWork;